/**
 * The grid behind typing data in, without the boxes.
 *
 * Kept apart from the screen for the same reason `fill-in.ts` is: the ways this goes
 * wrong are quiet ones. A blank row saved as a record prints an empty line on an
 * invoice; a paste that shifts by one column puts every amount under the wrong
 * heading. Neither is an error anybody sees, so they are pure functions with a test.
 */
import { entryText, newColumn, readEntry, type SourceColumn } from "@broadpaper/ingest";

/** A cell that does not read as its column's type, where the person can find it. */
export interface Problem {
  /** Index into the grid, which is the row number shown less one. */
  row: number;
  column: number;
  reason: string;
}

/** Stored rows as the text each cell shows. */
export function gridOf(columns: SourceColumn[], rows: Array<Record<string, unknown>>): string[][] {
  return rows.map((row) => columns.map((column) => entryText(column, row[column.name] ?? null)));
}

/**
 * The grid read back into rows, and every cell that would not read.
 *
 * A row with nothing in it is left out rather than saved: the grid always has one to
 * type into, and an empty record is a blank line in a table or a first row with no
 * client's name.
 */
export function rowsOf(columns: SourceColumn[], cells: string[][]): { rows: Array<Record<string, unknown>>; problems: Problem[] } {
  const rows: Array<Record<string, unknown>> = [];
  const problems: Problem[] = [];
  cells.forEach((line, r) => {
    if (line.every((text) => !text.trim())) return;
    const row: Record<string, unknown> = {};
    columns.forEach((column, c) => {
      const entry = readEntry(column, line[c] ?? "");
      if (entry.ok) row[column.name] = entry.value;
      else {
        row[column.name] = null;
        problems.push({ row: r, column: c, reason: entry.reason });
      }
    });
    rows.push(row);
  });
  return { rows, problems };
}

/**
 * Cells copied out of a spreadsheet, pasted from one cell onwards.
 *
 * Excel and Google Sheets put tab-separated rows on the clipboard, so a paste of three
 * rows by four columns fills three rows by four columns starting where the cursor is,
 * adding rows as it needs them and ignoring anything past the last column — rather than
 * all twelve values landing in one box. A value that reads as its column's type is
 * written the way the grid writes it, so a pasted `31/08/2026` shows in a date box.
 */
export function pasteAt(columns: SourceColumn[], cells: string[][], row: number, column: number, text: string): string[][] {
  const lines = text.replace(/\r\n?/g, "\n").replace(/\n$/, "").split("\n").map((line) => line.split("\t"));
  const next = cells.map((line) => [...line]);
  lines.forEach((values, i) => {
    const r = row + i;
    while (next.length <= r) next.push(columns.map(() => ""));
    values.forEach((value, j) => {
      const c = column + j;
      if (c >= columns.length) return;
      const entry = readEntry(columns[c]!, value);
      next[r]![c] = entry.ok && entry.value !== null ? entryText(columns[c]!, entry.value) : value.trim();
    });
  });
  return next;
}

/** Whether pasted text is more than one cell, and so should go across the grid. */
export function isGrid(text: string): boolean {
  return /\t|\n./.test(text.replace(/\r\n?/g, "\n").replace(/\n$/, ""));
}

/**
 * What an empty set of data starts as: something to type a line of an invoice into.
 *
 * Two columns rather than none, because a grid with no columns is a question before
 * anybody has typed anything, and most documents somebody makes by hand are a list of
 * things with an amount against each.
 */
export function emptyGrid(): { columns: SourceColumn[]; cells: string[][] } {
  const item = newColumn([], "Item");
  const amount = newColumn([item], "Amount", "currency");
  return { columns: [item, amount], cells: [0, 1, 2].map(() => ["", ""]) };
}
